import { ServerCog } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useDeployThinExit } from '@/hooks/use-deploy-thin-exit';
import { useFleetSummary } from '@/hooks/use-fleet-summary';
import { ApiError } from '@/lib/api-client';

/**
 * "Deploy thin exit" — a confirm dialog over
 * `POST /regions/:region/deploy-thin-exit`.
 *
 * Lets an operator manually scale out a region (the same "Deploy Thin Exit"
 * Semaphore playbook the autoscaler fires on VPNRegionLowCapacity) without
 * touching Semaphore directly. It provisions a server, so the operator must
 * pick the target region and confirm before it fires.
 *
 * The region is free text with a datalist of the fleet's known
 * countries/continents as SUGGESTIONS — the autoscaler's `region` label may use
 * its own slug vocabulary, so a custom value is always allowed.
 */
export function DeployThinExitDialog(): JSX.Element {
  const [open, setOpen] = useState(false);
  const [region, setRegion] = useState('');
  const [error, setError] = useState<string | null>(null);
  const deploy = useDeployThinExit();
  const summary = useFleetSummary();

  const suggestions = Array.from(
    new Set([
      ...(summary.data?.filters.countries ?? []),
      ...(summary.data?.filters.continents ?? []),
    ]),
  ).sort();

  function close(): void {
    setOpen(false);
    setRegion('');
    setError(null);
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const trimmed = region.trim();
    if (!trimmed) {
      setError('Enter a region to scale out.');
      return;
    }
    setError(null);

    deploy.mutate(trimmed, {
      onSuccess: (result) => {
        toast.success(`Deploy Thin Exit dispatched for ${result.region} (task #${result.task_id}).`);
        close();
      },
      onError: (err) => {
        setError(err instanceof ApiError ? err.message : 'Could not dispatch the deploy.');
      },
    });
  }

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        <ServerCog className="mr-1 h-4 w-4" aria-hidden />
        Deploy thin exit
      </Button>

      <Dialog open={open} onOpenChange={(next) => (next ? setOpen(true) : close())}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Deploy thin exit</DialogTitle>
            <DialogDescription>
              Dispatches the <span className="font-mono">Deploy Thin Exit</span> playbook to add an
              extra exit/edge in a region — the same remediation the autoscaler fires on{' '}
              <span className="font-mono">VPNRegionLowCapacity</span>. This provisions a new server;
              confirm the target region below.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
            <div className="space-y-1.5">
              <Label htmlFor="thin-exit-region">Region</Label>
              <Input
                id="thin-exit-region"
                list="thin-exit-region-options"
                required
                autoFocus
                value={region}
                onChange={(e) => {
                  setRegion(e.target.value);
                  setError(null);
                }}
                placeholder="e.g. eu, de, us-east"
                className="font-mono"
              />
              <datalist id="thin-exit-region-options">
                {suggestions.map((r) => (
                  <option key={r} value={r} />
                ))}
              </datalist>
              <p className="text-xs text-muted-foreground">
                The region tag the autoscaler groups exits by. Known fleet regions are suggested;
                type a custom tag if yours isn&apos;t listed.
              </p>
            </div>

            {error && (
              <p className="text-sm text-destructive" role="alert">
                {error}
              </p>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={close} disabled={deploy.isPending}>
                Cancel
              </Button>
              <Button type="submit" disabled={deploy.isPending || region.trim().length === 0}>
                {deploy.isPending ? 'Dispatching…' : 'Deploy thin exit'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
